import { isEmail } from '@ardoq/validation';
import { TextInput } from '@ardoq/forms';
import { Box, Stack } from '@ardoq/layout';
import { Button } from '@ardoq/button';
import { useReducer } from 'react';

type Link = {
  href: string;
  text: string;
};

type HandleSubmit = (link: Link) => void;

export type LinkFormLabels = {
  linkLabel: string;
  displayTextLabel: string;
  insertButton: string;
  updateButton: string;
  urlRequiredError: string;
  urlInvalidError: string;
};

export const defaultLinkFormLabels: LinkFormLabels = {
  linkLabel: 'Link',
  displayTextLabel: 'Display text (optional)',
  insertButton: 'Insert link',
  updateButton: 'Update link',
  urlRequiredError: 'Please fill in the URL field',
  urlInvalidError: 'Please enter a valid URL',
};

interface LinkFormProps {
  isActive: boolean;
  onSubmit: HandleSubmit;
  getInitialState: () => Link;
  labels?: LinkFormLabels;
}

type LinkFormState = {
  errorMessage?: string;
} & Link;

type LinkFormAction =
  | { type: 'SET_HREF'; payload: string }
  | { type: 'SET_TEXT'; payload: string }
  | { type: 'SET_ERROR'; payload: string | undefined }
  | { type: 'RESET' };

const initialState: LinkFormState = {
  href: '',
  text: '',
  errorMessage: undefined,
};

const linkFormReducer = (
  state: LinkFormState,
  action: LinkFormAction
): LinkFormState => {
  switch (action.type) {
    case 'SET_HREF':
      return { ...state, href: action.payload };
    case 'SET_TEXT':
      return { ...state, text: action.payload };
    case 'SET_ERROR':
      return { ...state, errorMessage: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const validProtocols = ['http:', 'https:', 'mailto:'];
    if (!validProtocols.includes(urlObj.protocol)) {
      return false;
    }
    if (!urlObj.hostname && urlObj.protocol !== 'mailto:') {
      return false;
    }
    if (urlObj.protocol === 'mailto:' && !isEmail(urlObj.pathname)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const LinkForm = ({
  isActive,
  onSubmit,
  getInitialState,
  labels = defaultLinkFormLabels,
}: LinkFormProps) => {
  const [state, dispatch] = useReducer(
    linkFormReducer,
    {
      ...initialState,
    },
    (): LinkFormState => getInitialState()
  );

  const handleSubmit = () => {
    if (state.href === '') {
      dispatch({ type: 'SET_ERROR', payload: labels.urlRequiredError });
      return;
    }
    if (!isValidUrl(state.href)) {
      dispatch({ type: 'SET_ERROR', payload: labels.urlInvalidError });
      return;
    }
    onSubmit({ href: state.href, text: state.text });
  };

  return (
    <Box padding="medium" minWidth="280px">
      <Stack gap="medium">
        <TextInput
          label={labels.linkLabel}
          value={state.href}
          autoFocus
          onValueChange={value =>
            dispatch({ type: 'SET_HREF', payload: value })
          }
          hasError={!!state.errorMessage}
          errorMessage={state.errorMessage}
        />
        <TextInput
          label={labels.displayTextLabel}
          value={state.text}
          onValueChange={value =>
            dispatch({ type: 'SET_TEXT', payload: value })
          }
        />
        <Box width="fit-content">
          <Button onClick={handleSubmit}>
            {isActive ? labels.updateButton : labels.insertButton}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
